import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { noop } from 'lodash';
import { FieldArray, FormikContext, useFormik } from 'formik';
// Import Components
import { Button } from '@geneui/components';
import ButtonsRow from './ButtonsRow';
// Import Types
import type { ButtonBuilderFormFields, ButtonsRow as ButtonsRowType } from '../types';
// Import Constants
import l from 'constants/localizationLabels';
import { BUTTON_ROWS_FORM_KEY } from '../constants';
// Import Validations
import { useButtonBuilderValidation } from '../validation';
// Import Styles
import styles from './buttonRowsBuilder.module.scss';

interface Props {
    onChange: (values: ButtonsRowType[], isValid: boolean) => void;
    initialData: ButtonsRowType[];
    onFocus: () => void;
}

const ButtonRowsBuilder: FC<Props> = ({ onChange, initialData, onFocus }) => {
    const { t } = useTranslation();

    const initialValues = useMemo(
        () => ({
            [BUTTON_ROWS_FORM_KEY]: initialData,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const validationSchema = useButtonBuilderValidation();

    const formik = useFormik<ButtonBuilderFormFields>({
        initialValues,
        validateOnMount: true,
        validateOnChange: true,
        validateOnBlur: true,
        enableReinitialize: true,
        onSubmit: noop,
        validationSchema,
    });

    const { values, errors } = formik;

    useEffect(() => {
        onChange(values[BUTTON_ROWS_FORM_KEY], !errors[BUTTON_ROWS_FORM_KEY]);
    }, [errors, onChange, values]);

    return (
        <FormikContext.Provider value={formik}>
            <FieldArray
                name={BUTTON_ROWS_FORM_KEY}
                render={({ push, handleRemove }) => {
                    const rows = values[BUTTON_ROWS_FORM_KEY];
                    const handleAddButtonRow = () => {
                        if (rows.length === 1 && rows[0].length === 0) {
                            handleRemove(0)();
                        }

                        push([{ url: '', text: '' }]);
                    };

                    return (
                        <>
                            <div>
                                <Button
                                    flexibility="default"
                                    appearance="outline"
                                    size="medium"
                                    icon="bc-icon-plus"
                                    onClick={handleAddButtonRow}
                                    disabled={rows.length >= 4}
                                >
                                    {t(l.AddButtonsRow)}
                                </Button>
                            </div>
                            <div className={styles.scrollableButtonsGroupContainer}>
                                {rows.map((_, index) => (
                                    <ButtonsRow
                                        key={index}
                                        rowIndex={index}
                                        handleRemoveRow={handleRemove}
                                        onFocus={onFocus}
                                    />
                                ))}
                            </div>
                        </>
                    );
                }}
            />
        </FormikContext.Provider>
    );
};

export default ButtonRowsBuilder;
